import React, { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../api';
import {
  trackContactFormStart,
  trackContactFormSubmit,
  trackDiscoveryCallStarted,
  getStoredUtmParams,
  trackLinkClick,
} from '../lib/analytics';
import BRAND from '../config/brand';
import { MEETING_BOOKING_TYPE, getMeetingBookingHref } from '../config/booking';
import {
  GOAL_SETTING_MEETING,
  OFFER_IDS,
  PLANNING_MEETING_REFUND_WORDING,
  PRICES,
  SERVICES_PATH,
  getOfferById,
} from '../config/planningOffers';
import PublicSiteNav from './PublicSiteNav';
import './PublicLinksPage.css';

const FORM_NAME = 'discovery_contact';

/** Booking page with stored UTMs attached (no form/PII contents). */
function bookingUrlWithAttribution() {
  return getMeetingBookingHref(getStoredUtmParams());
}

const ageRanges = ['Under 30', '30-39', '40-49', '50-59', '60+'];
const maritalStatuses = ['Single', 'Married', 'Partnered', 'Divorced/Widowed', 'Prefer not to say'];
const incomeRanges = ['Under $150k', '$150k-$250k', '$250k-$400k', '$400k-$750k', '$750k+'];
const planningConcerns = [
  'Retirement planning',
  'Tax planning',
  'Home purchase affordability',
  'Equity compensation / RSUs',
  'Job change / compensation decision',
  'Budgeting / cash flow',
  'Investment allocation',
  'Estate / insurance planning',
];

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  ageRange: '',
  maritalStatus: '',
  householdIncomeRange: '',
  planningConcerns: [],
  message: '',
  consent: false,
  smsConsent: false,
};

const calculatorContextLabels = {
  paycheck_tax_estimator: 'Tax Readiness Check',
  retirement_gap_estimator: 'Retirement Gap Estimator',
  social_security_estimator: 'Social Security Planning Calculator',
  roth_vs_traditional_calculator: 'Roth vs. Traditional IRA Calculator',
  housing_decision_calculator: 'Rent vs Buy Housing Calculator',
};

function readLeadContext(search) {
  const params = new URLSearchParams(search || '');
  const offerId = params.get('offer') || '';
  const offer = offerId ? getOfferById(offerId) : null;
  return {
    cta: params.get('cta') || '',
    calculatorId: params.get('calculator_id') || '',
    calculatorName: params.get('calculator_name') || '',
    offerId,
    pricingModel: params.get('pricing_model') || offer?.pricingModel || '',
    contentCategory: params.get('content_category') || '',
    promotionalAngle: params.get('promotional_angle') || '',
    offer,
  };
}

function initialFormForSearch(search) {
  const context = readLeadContext(search);
  const calculatorLabel = calculatorContextLabels[context.calculatorId] || context.calculatorName;
  const parts = [];

  if (calculatorLabel) {
    parts.push(
      context.cta === 'send_result'
        ? `I used the ${calculatorLabel} and would like help interpreting my result.`
        : `I used the ${calculatorLabel} and would like to discuss a comprehensive financial plan.`
    );
  }

  if (context.offer) {
    parts.push(`I am interested in: ${context.offer.title} (${context.offer.priceLabel}).`);
  }

  if (!parts.length) return initialForm;

  return {
    ...initialForm,
    message: parts.join(' '),
  };
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function FinancialPlanningLeadFormPage() {
  const location = useLocation();
  const leadContext = readLeadContext(location.search);
  const isComprehensivePlan = leadContext.offerId === OFFER_IDS.COMPREHENSIVE;
  const [form, setForm] = useState(() => initialFormForSearch(location.search));
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const formStartedRef = useRef(false);

  const buildAttributionParams = useCallback((ctaLocation = 'intake_form') => ({
    selected_offer: leadContext.offerId || undefined,
    offer_id: leadContext.offerId || undefined,
    pricing_model: leadContext.pricingModel || undefined,
    content_category: leadContext.contentCategory || undefined,
    promotional_angle: leadContext.promotionalAngle || undefined,
    cta_location: ctaLocation,
  }), [
    leadContext.offerId,
    leadContext.pricingModel,
    leadContext.contentCategory,
    leadContext.promotionalAngle,
  ]);

  const markFormStarted = useCallback(() => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackContactFormStart(FORM_NAME, buildAttributionParams('intake_form'));
  }, [buildAttributionParams]);

  const updateField = (field, value) => {
    markFormStarted();
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleConcern = (concern) => {
    markFormStarted();
    setForm((prev) => {
      const exists = prev.planningConcerns.includes(concern);
      return {
        ...prev,
        planningConcerns: exists
          ? prev.planningConcerns.filter((item) => item !== concern)
          : [...prev.planningConcerns, concern],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const fullName = form.fullName.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();

    if (!fullName || !isValidEmail(email)) {
      setError('Please enter your full name and a valid email address.');
      return;
    }

    if (phone && !form.smsConsent) {
      setError('If you provide a mobile number, please confirm SMS permission below (or leave phone blank).');
      return;
    }

    if (!form.consent) {
      setError('Please confirm the consent statement before submitting.');
      return;
    }

    setStatus('submitting');

    try {
      await api.post('/api/public/planning-leads', {
        ...form,
        fullName,
        email,
        phone: form.phone.trim(),
        message: form.message.trim(),
        smsConsent: form.smsConsent === true,
        attribution: {
          ...getStoredUtmParams(),
          ...(leadContext.offerId ? { selected_offer: leadContext.offerId } : {}),
          ...(leadContext.pricingModel ? { pricing_model: leadContext.pricingModel } : {}),
          ...(leadContext.contentCategory ? { content_category: leadContext.contentCategory } : {}),
          ...(leadContext.promotionalAngle ? { promotional_angle: leadContext.promotionalAngle } : {}),
        },
      });
      const submitAttribution = buildAttributionParams('intake_form');
      trackLinkClick('/financial-planning-services/request-meeting', 'Submit Goal-Setting Meeting Request', {
        destination_type: 'contact_form',
        button_id: 'discovery_contact_submit',
        button_label: 'Submit Goal-Setting Meeting Request',
        ...submitAttribution,
      });
      // Successful lead submission — not a CTA click and not a confirmed booking.
      trackContactFormSubmit(FORM_NAME, submitAttribution);
      setStatus('success');
      setForm(initialFormForSearch(location.search));
      formStartedRef.current = false;
    } catch (requestError) {
      const message =
        requestError.response?.data?.error ||
        'Something went wrong while sending your request. Please try again.';
      setError(message);
      setStatus('idle');
    }
  };

  return (
    <div className="public-linktree-page public-service-page">
      <PublicSiteNav />
      <main className="public-linktree-shell public-service-shell public-lead-shell">
        <section className="public-service-header public-lead-header">
          <Link className="public-service-logo" to={SERVICES_PATH} aria-label="Back to Services & Pricing">
            <img src={BRAND.logoSrc} alt="Bautista Planning and Analytics" />
          </Link>
          <p className="public-linktree-kicker">
            {isComprehensivePlan ? 'Comprehensive Financial Plan' : 'Financial planning intake'}
          </p>
          <h1>
            {isComprehensivePlan
              ? 'Tell us about the financial life you want to organize.'
              : 'Tell us what you want your financial plan to accomplish.'}
          </h1>
          <p>
            Share a few details about your household, priorities, and financial questions so the planning process can begin with the right context.
          </p>
          {isComprehensivePlan ? (
            <p className="public-lead-form-note" style={{ marginTop: '0.85rem' }}>
              Start with a {PRICES.planningMeeting} {GOAL_SETTING_MEETING.name}. {PLANNING_MEETING_REFUND_WORDING}
            </p>
          ) : null}
          {leadContext.offer ? (
            <p className="public-offer-price" style={{ marginTop: '0.85rem' }}>
              Selected interest: {leadContext.offer.title} — {leadContext.offer.priceLabel}
            </p>
          ) : null}
        </section>

        <section className="public-service-panel public-lead-card" aria-labelledby="planning-lead-form-heading">
          {status === 'success' ? (
            <div className="public-lead-success">
              <p className="public-linktree-kicker">Request received</p>
              <h2 id="planning-lead-form-heading">Thank you — your request has been received.</h2>
              <p>
                Next, pick a time for your {GOAL_SETTING_MEETING.name}. It’s {PRICES.planningMeeting} for{' '}
                {GOAL_SETTING_MEETING.durationMinutes} minutes, paid when you book. {PLANNING_MEETING_REFUND_WORDING}
              </p>
              <div className="public-service-actions public-service-actions--centered">
                <Link
                  className="public-service-cta public-service-cta--primary"
                  to={bookingUrlWithAttribution()}
                  onClick={() => {
                    const handoffUrl = bookingUrlWithAttribution();
                    const handoffParams = {
                      ...buildAttributionParams('intake_success_booking'),
                      button_id: 'discovery_contact_schedule_booking',
                      button_label: 'Schedule Your Goal-Setting Meeting',
                      booking_type: MEETING_BOOKING_TYPE,
                    };
                    trackLinkClick(handoffUrl, 'Schedule Your Goal-Setting Meeting', {
                      destination_type: 'booking',
                      ...handoffParams,
                    });
                    trackDiscoveryCallStarted(handoffUrl, 'Schedule Your Goal-Setting Meeting', handoffParams);
                  }}
                >
                  Schedule Your Goal-Setting Meeting
                </Link>
              </div>
            </div>
          ) : (
            <form className="public-lead-form" onSubmit={handleSubmit}>
              <div>
                <p className="public-linktree-kicker">Planning intake</p>
                <h2 id="planning-lead-form-heading">
                  Get Started With Your Financial Plan
                </h2>
                <p className="public-lead-form-note">
                  Please do not include Social Security numbers, account numbers, dates of birth,
                  addresses, or uploads.
                </p>
              </div>

              {error && <div className="public-lead-error">{error}</div>}

              <div className="public-lead-grid">
                <label>
                  Full name <span>*</span>
                  <input
                    type="text"
                    autoComplete="name"
                    value={form.fullName}
                    onFocus={markFormStarted}
                    onChange={(event) => updateField('fullName', event.target.value)}
                    required
                  />
                </label>
                <label>
                  Email <span>*</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onFocus={markFormStarted}
                    onChange={(event) => updateField('email', event.target.value)}
                    required
                  />
                </label>
                <label>
                  Phone
                  <input
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onFocus={markFormStarted}
                    onChange={(event) => updateField('phone', event.target.value)}
                  />
                  <small className="public-lead-field-hint">
                    Optional. If provided, confirm SMS permission in the section below.
                  </small>
                </label>
                <label>
                  Age range
                  <select
                    value={form.ageRange}
                    onFocus={markFormStarted}
                    onChange={(event) => updateField('ageRange', event.target.value)}
                  >
                    <option value="">Select one</option>
                    {ageRanges.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Marital status
                  <select
                    value={form.maritalStatus}
                    onFocus={markFormStarted}
                    onChange={(event) => updateField('maritalStatus', event.target.value)}
                  >
                    <option value="">Select one</option>
                    {maritalStatuses.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Household income range
                  <select
                    value={form.householdIncomeRange}
                    onFocus={markFormStarted}
                    onChange={(event) => updateField('householdIncomeRange', event.target.value)}
                  >
                    <option value="">Select one</option>
                    {incomeRanges.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
              </div>

              <fieldset className="public-lead-checkboxes">
                <legend>Top planning concerns</legend>
                <div>
                  {planningConcerns.map((concern) => (
                    <label key={concern}>
                      <input
                        type="checkbox"
                        checked={form.planningConcerns.includes(concern)}
                        onChange={() => toggleConcern(concern)}
                      />
                      {concern}
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="public-lead-message">
                Optional message
                <textarea
                  rows="5"
                  value={form.message}
                  onFocus={markFormStarted}
                  onChange={(event) => updateField('message', event.target.value)}
                  placeholder="What decisions or questions would you like to talk through?"
                />
              </label>

              <section className="public-lead-sms-section" aria-labelledby="planning-lead-sms-heading">
                <p className="public-linktree-kicker">Text messages</p>
                <h3 id="planning-lead-sms-heading">Permission to text you</h3>
                <p className="public-lead-sms-intro">
                  If you share a mobile number, Bautista Planning and Analytics may send a small number of
                  text messages about your inquiry — for example scheduling your Goal-Setting Meeting or
                  following up on next steps. We do not send account details, balances, or marketing
                  blasts by text.
                </p>
                <ul className="public-lead-sms-list">
                  <li>Messages are optional and only sent if you opt in below.</li>
                  <li>Message and data rates may apply.</li>
                  <li>Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance.</li>
                  <li>Consent is not required to submit this form or receive planning services by email or phone.</li>
                </ul>
                <label className="public-lead-consent public-lead-sms-consent">
                  <input
                    type="checkbox"
                    checked={form.smsConsent}
                    onChange={(event) => updateField('smsConsent', event.target.checked)}
                  />
                  <span>
                    {form.phone.trim()
                      ? 'I agree to receive text messages from Bautista Planning and Analytics at the mobile number I provided about this inquiry. I understand I can reply STOP to opt out.'
                      : 'If I add a mobile number above, I agree to receive text messages from Bautista Planning and Analytics about this inquiry. I understand I can reply STOP to opt out.'}
                  </span>
                </label>
              </section>

              <label className="public-lead-consent">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(event) => updateField('consent', event.target.checked)}
                  required
                />
                <span>
                  I understand that submitting this form does not create an advisory relationship
                  and that Bautista Planning and Analytics will contact me about my inquiry.
                </span>
              </label>

              <button className="public-service-cta public-service-cta--primary public-lead-submit" type="submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Sending Request...' : 'Submit Request'}
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
